import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import i18n from "@/i18n";

/**
 * Deny-Pfad (401): externe Identität ohne Gruppen-Mapping → graceful Sign-out + Toast.
 * Modul-intern (kein Export) — Verhalten wird über provisionExternalIdentity (401-Pfad) getestet.
 */
async function handleExternalAccessDenied(): Promise<void> {
  logger.warn("External SSO user has no group mapping — denying access (signing out)");
  await supabase.auth.signOut();
  toast.error(
    i18n.t("auth.externalAccessDenied", {
      defaultValue:
        "Zugriff verweigert: Ihre externe Identität ist keiner Berechtigungskategorie zugeordnet.",
    }),
  );
}

/**
 * JIT-provision external Entra B2B users after a successful SSO login (TES-598).
 *
 * Non-blocking and best-effort: invokes the `jit-provision-external-user` Edge Function with
 * the user's own access token. The Edge Function verifies the JWT, derives external_category
 * server-side from the token's Entra group claims (chk_external_group_mapping) and — only on a
 * match — sets profiles.external_user/external_category/entra_oid via a service-role RPC.
 *
 * Internal users are a 200 no-op (provisioned:false, internal:true) → login proceeds normally.
 * A 401 means an external claim was present but NO group is mapped (access denied): we sign the
 * user out so they don't land in a half-authenticated state. Any transport/parse error is
 * swallowed — provisioning must never break a normal (internal) login.
 */
export async function provisionExternalIdentity(accessToken: string): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke("jit-provision-external-user", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) {
      // supabase-js surfaces non-2xx as FunctionsHttpError. 401 = external claim without a group
      // mapping → end the session gracefully; any other error is swallowed (non-blocking).
      const status = (error as { context?: { status?: number } }).context?.status;
      if (status === 401) await handleExternalAccessDenied();
      else logger.warn("JIT external provisioning call failed (non-blocking):", error);
      return;
    }

    if (!data) {
      logger.warn("JIT external provisioning returned no data (non-blocking)");
      return;
    }

    if ((data as { provisioned?: boolean } | null)?.provisioned) {
      logger.info("External SSO user provisioned", {
        external_category: (data as { external_category?: string }).external_category,
      });
    }
  } catch (err) {
    // Never block the auth flow on provisioning failures.
    logger.warn("JIT external provisioning errored (non-blocking):", err);
  }
}
